import { useState, useEffect, useMemo } from "react";
import { usePaged, Paginator } from "@/components/Paginator";
import { useFetch } from "@/hooks/useFetch";
import api, { apiError } from "@/lib/api";
import { Loading, ErrorState, Empty } from "@/components/DataState";
import { brl, num, km, MONTHS, fmtDate, inRange } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatCard } from "@/components/StatCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PeriodSelector } from "@/components/PeriodSelector";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Fuel, Utensils, Bus, Download, Gauge, FileText, Save, SlidersHorizontal, Gift, Trophy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { usePayrollSettings } from "@/context/PayrollSettingsContext";
import { computeAutoBonus } from "@/lib/bonusConfig";
import { useContractTypes } from "@/lib/useContractTypes";
import { useOutOfPayBase } from "@/lib/usePayBases";
import { useMinsFor, useBaseConfigs } from "@/lib/useBaseMins";
import { useBase } from "@/context/BaseContext";
import { useReportLogo } from "@/lib/useLogo";
import { queryClient } from "@/lib/queryClient";
import panelApi from "@/lib/panelApi";
import { useQuery } from "@tanstack/react-query";

const FREQ_OPTIONS = [
  { v: "quinzena", label: "Aplicar por quinzena" },
  { v: "q1", label: "Aplicar somente na 1ª quinzena" },
  { v: "q2", label: "Aplicar somente na 2ª quinzena" },
];
const FREQ_HINT = {
  quinzena: "Valor de uma quinzena (metade do mês).",
  q1: "Valor mensal completo, aplicado apenas na 1ª quinzena.",
  q2: "Valor mensal completo, aplicado apenas na 2ª quinzena.",
};
const prodKey = (name) => `prod_${String(name || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "")}`;
const useBenefitFrequencies = () => useQuery({
  queryKey: ["panel-benefit-frequencies"],
  queryFn: async () => (await panelApi.get("/panel/benefit-frequencies")).data?.frequencies || {},
});

const now = new Date();

function BenefitDetailModal({ driver, periodText, start, end, prodQ1, prodQ2, levelQ1, levelQ2, useQ1, useQ2, onClose }) {
  const days = (driver.days || [])
    .filter((dy) => inRange(dy.date, start, end))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  const events = [];
  const prodEvents = [];
  if (useQ1 && prodQ1 > 0) prodEvents.push({ key: "prod-q1", date: "1ª quinzena", type: "Produtividade", color: "text-rose-300", desc: levelQ1 || "Bônus", value: prodQ1 });
  if (useQ2 && prodQ2 > 0) prodEvents.push({ key: "prod-q2", date: "2ª quinzena", type: "Produtividade", color: "text-rose-300", desc: levelQ2 || "Bônus", value: prodQ2 });
  days.forEach((dy) => {
    if ((dy.fuel || 0) > 0) events.push({ key: `${dy.date}-fuel`, date: dy.date, type: "Combustível", color: "text-orange-300", desc: km(dy.km), value: dy.fuel });
    if ((dy.meal || 0) > 0) events.push({ key: `${dy.date}-meal`, date: dy.date, type: "Refeição", color: "text-emerald-300", desc: dy.meal_eligible ? "Dia elegível" : "", value: dy.meal });
    if ((dy.commute || 0) > 0) events.push({ key: `${dy.date}-commute`, date: dy.date, type: "Deslocamento", color: "text-sky-300", desc: `${dy.commute_count || 1}× deslocamento`, value: dy.commute });
  });
  const all = [...prodEvents, ...events];
  const tot = {
    fuel: days.reduce((a, d) => a + (d.fuel || 0), 0),
    meal: days.reduce((a, d) => a + (d.meal || 0), 0),
    commute: days.reduce((a, d) => a + (d.commute || 0), 0),
    prod: (useQ1 ? prodQ1 : 0) + (useQ2 ? prodQ2 : 0),
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl border-slate-800 bg-slate-900" data-testid="ben-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-100"><Gift className="h-5 w-5 text-orange-400" /> Benefícios — {driver.name}</DialogTitle>
          <DialogDescription className="text-slate-400">Período: {periodText} · {all.length} evento(s)</DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-y-auto rounded-xl border border-slate-800">
          <table className="w-full text-sm" data-testid="ben-detail-table">
            <thead className="sticky top-0 bg-slate-900">
              <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2 font-semibold">Data</th>
                <th className="px-3 py-2 font-semibold">Tipo</th>
                <th className="px-3 py-2 font-semibold">Descrição</th>
                <th className="px-3 py-2 text-right font-semibold">Valor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {all.length === 0 && <tr><td colSpan={4}><Empty label="Sem benefícios no período" /></td></tr>}
              {all.map((e) => (
                <tr key={e.key} className="hover:bg-slate-800/40" data-testid={`ben-detail-row-${e.key}`}>
                  <td className="px-3 py-2 text-slate-300">{String(e.date).includes("quinzena") ? e.date : fmtDate(e.date)}</td>
                  <td className={`px-3 py-2 font-medium ${e.color}`}>{e.type}</td>
                  <td className="px-3 py-2 text-slate-400">{e.desc || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-200">{brl(e.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="ben-detail-totals">
          <div className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-slate-500">Combustível</p><p className="font-mono text-sm text-orange-300" data-testid="ben-detail-total-fuel">{brl(tot.fuel)}</p></div>
          <div className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-slate-500">Refeição</p><p className="font-mono text-sm text-emerald-300" data-testid="ben-detail-total-meal">{brl(tot.meal)}</p></div>
          <div className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-slate-500">Deslocamento</p><p className="font-mono text-sm text-sky-300" data-testid="ben-detail-total-commute">{brl(tot.commute)}</p></div>
          <div className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-slate-500">Produtividade</p><p className="font-mono text-sm text-rose-300" data-testid="ben-detail-total-prod">{brl(tot.prod)}</p></div>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-orange-500/30 bg-orange-500/10 px-3 py-2" data-testid="ben-detail-total-all">
          <span className="text-sm font-semibold text-orange-200">Total de benefícios</span>
          <span className="font-mono text-base font-bold text-orange-300">{brl(tot.fuel + tot.meal + tot.commute + tot.prod)}</span>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-slate-700 text-slate-300" onClick={onClose} data-testid="ben-detail-close">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Report() {
  const logo = useReportLogo();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const year = start ? Number(start.slice(0, 4)) : now.getFullYear();
  const month = start ? Number(start.slice(5, 7)) : now.getMonth() + 1;
  const { data, loading, error, refetch } = useFetch("/admin/benefits-report", { year, month });
  const reports1 = useFetch("/admin/reports", { year, month, quinzena: 1 });
  const reports2 = useFetch("/admin/reports", { year, month, quinzena: 2 });
  const cfg = useFetch("/config");
  const tiersData = useFetch("/admin/bonus-tiers");
  const { settings } = usePayrollSettings();

  // Produtividade é por quinzena: 1ª (dias 1-15), 2ª (16-fim), Mensal soma as duas.
  const sDay = start ? Number(start.slice(8, 10)) : 1;
  const eDay = end ? Number(end.slice(8, 10)) : 31;
  const useQ1 = sDay <= 15;
  const useQ2 = eDay >= 16;

  const mins = useMemo(() => ({ ...(cfg.data || {}), mestre_min: cfg.data?.mestre_min ?? settings.mestre_min }), [cfg.data, settings.mestre_min]);
  const minsFor = useMinsFor();
  const contractOf = useContractTypes();
  const tiers = useMemo(() => tiersData.data?.tiers || [], [tiersData.data]);
  const q1By = useMemo(() => { const m = {}; (reports1.data?.rows || []).forEach((r) => { m[r.id] = r.deliveries || 0; }); return m; }, [reports1.data]);
  const q2By = useMemo(() => { const m = {}; (reports2.data?.rows || []).forEach((r) => { m[r.id] = r.deliveries || 0; }); return m; }, [reports2.data]);
  const bonusOf = (id) => {
    let b = 0;
    if (useQ1) b += computeAutoBonus(q1By[id] || 0, minsFor(id, mins), tiers, contractOf(id)).value;
    if (useQ2) b += computeAutoBonus(q2By[id] || 0, minsFor(id, mins), tiers, contractOf(id)).value;
    return b;
  };

  // Combustível/Refeição/Deslocamento por quinzena, somando os dias (exibição apenas).
  const totalsOf = (d) => {
    if (!Array.isArray(d.days)) return d.totals || {};
    const t = { fuel: 0, meal: 0, commute: 0, km: 0 };
    d.days.forEach((dy) => {
      const day = Number(String(dy.date).slice(8, 10));
      if (!((useQ1 && day <= 15) || (useQ2 && day >= 16))) return;
      t.fuel += dy.fuel || 0; t.meal += dy.meal || 0; t.commute += dy.commute || 0; t.km += dy.km || 0;
    });
    return t;
  };
  const outOfBase = useOutOfPayBase();
  const drivers = useMemo(() => (data?.drivers || []).filter((d) => !outOfBase(d.id)), [data, outOfBase]);
  const pg = usePaged(drivers, 25);
  const totals = drivers.reduce((a, d) => {
    const t = totalsOf(d);
    a.fuel += t.fuel || 0; a.meal += t.meal || 0; a.commute += t.commute || 0; a.km += t.km || 0;
    a.prod += bonusOf(d.id);
    return a;
  }, { fuel: 0, meal: 0, commute: 0, km: 0, prod: 0 });

  const [detail, setDetail] = useState(null);

  const exportCsv = () => {
    const rows = [["Nome", "Veiculo", "Combustivel", "Refeicao", "Deslocamento", "KM", "Produtividade", "Total"]];
    drivers.forEach((d) => { const t = totalsOf(d); const b = bonusOf(d.id); rows.push([d.name, d.vehicle_type || d.category || "", t.fuel || 0, t.meal || 0, t.commute || 0, t.km || 0, b, (t.fuel || 0) + (t.meal || 0) + (t.commute || 0) + b]); });
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `beneficios_${year}_${month}.csv`; a.click();
  };

  const esc = (s) =>
    String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

  const printPdf = () => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Permita pop-ups para gerar o PDF"); return; }
    const rows = drivers.map((d) => { const t = totalsOf(d); const tot = (t.fuel || 0) + (t.meal || 0) + (t.commute || 0) + bonusOf(d.id); return `<tr><td>${esc(d.name)}</td><td>${esc(brl(t.fuel))}</td><td>${esc(brl(t.meal))}</td><td>${esc(brl(t.commute))}</td><td>${esc(km(t.km))}</td><td>${esc(brl(bonusOf(d.id)))}</td><td><b>${esc(brl(tot))}</b></td></tr>`; }).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Relatorio de Beneficios</title>
      <style>body{font-family:Arial,sans-serif;padding:32px;color:#111}h1{margin:0 0 4px}p{color:#555;margin:0 0 20px}
      table{width:100%;border-collapse:collapse;font-size:13px}th,td{border:1px solid #ddd;padding:8px;text-align:left}
      th{background:#E30613;color:#fff}tfoot td{font-weight:bold;background:#f8fafc}.logo{float:right;max-height:56px;max-width:180px}</style></head>
      <body>${logo?.data ? `<img class="logo" src="${logo.data}" alt="Logo">` : ""}<h1>Central do Courier — Relatorio de Beneficios</h1>
      <p>${esc(data?.label || `${MONTHS[month - 1]}/${year}`)} • ${drivers.length} colaboradores</p>
      <table><thead><tr><th>Courier</th><th>Combustivel</th><th>Refeicao</th><th>Deslocamento</th><th>KM</th><th>Produtividade</th><th>Total</th></tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr><td>TOTAIS</td><td>${esc(brl(totals.fuel))}</td><td>${esc(brl(totals.meal))}</td><td>${esc(brl(totals.commute))}</td><td>${esc(km(totals.km))}</td><td>${esc(brl(totals.prod))}</td><td>${esc(brl(totals.fuel + totals.meal + totals.commute + totals.prod))}</td></tr></tfoot>
      </table></body></html>`;
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    w.location.href = url;
    setTimeout(() => { try { w.focus(); w.print(); } catch (e) {} URL.revokeObjectURL(url); }, 400);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <PeriodSelector start={start} end={end} setStart={setStart} setEnd={setEnd} testPrefix="ben-period" />
        <Button variant="outline" className="border-slate-700 text-slate-300" onClick={exportCsv} data-testid="ben-export"><Download className="h-4 w-4" /> CSV</Button>
        <Button className="bg-orange-500 text-white hover:bg-orange-600" onClick={printPdf} data-testid="ben-pdf"><FileText className="h-4 w-4" /> Gerar PDF</Button>
        <span className="text-xs text-slate-500" data-testid="ben-monthly-hint">Valores filtrados pela quinzena selecionada · Mensal soma as duas</span>
        <span className="ml-auto text-sm text-slate-500">{data?.label}</span>
      </div>

      {loading || !outOfBase.ready ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            <StatCard icon={Fuel} label="Combustível" value={brl(totals.fuel)} accent="orange" testId="ben-fuel" />
            <StatCard icon={Utensils} label="Vale refeição" value={brl(totals.meal)} accent="emerald" testId="ben-meal" />
            <StatCard icon={Bus} label="Deslocamento" value={brl(totals.commute)} accent="sky" testId="ben-commute" />
            <StatCard icon={Trophy} label="Produtividade" value={brl(totals.prod)} accent="rose" testId="ben-prod" />
            <StatCard icon={Gauge} label="KM total" value={km(totals.km)} accent="violet" testId="ben-km" />
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900">
            <table className="w-full text-sm" data-testid="ben-table">
              <thead>
                <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 font-semibold">Courier</th>
                  <th className="px-4 py-3 font-semibold">Combustível</th><th className="px-4 py-3 font-semibold">Refeição</th>
                  <th className="px-4 py-3 font-semibold">Deslocamento</th><th className="px-4 py-3 font-semibold">KM</th>
                  <th className="px-4 py-3 font-semibold">Produtividade</th><th className="px-4 py-3 font-semibold">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {drivers.length === 0 && <tr><td colSpan={7}><Empty /></td></tr>}
                {pg.items.map((d) => { const t = totalsOf(d); return (
                  <tr key={d.id} className="hover:bg-slate-800/40">
                    <td className="px-4 py-3">
                      <button onClick={() => setDetail(d)} data-testid={`ben-name-${d.id}`} className="font-medium text-slate-100 underline-offset-2 hover:text-orange-300 hover:underline">{d.name}</button>
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-200">{brl(t.fuel)}</td>
                    <td className="px-4 py-3 font-mono text-slate-200">{brl(t.meal)}</td>
                    <td className="px-4 py-3 font-mono text-slate-200">{brl(t.commute)}</td>
                    <td className="px-4 py-3 font-mono text-slate-400">{km(t.km)}</td>
                    <td className="px-4 py-3 font-mono text-amber-300" data-testid={`ben-prod-${d.id}`}>{brl(bonusOf(d.id))}</td>
                    <td className="px-4 py-3 font-mono font-semibold text-white" data-testid={`ben-total-${d.id}`}>{brl(t.fuel + t.meal + t.commute + bonusOf(d.id))}</td>
                  </tr>
                ); })}
              </tbody>
              {drivers.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-700 bg-slate-950/60 font-semibold" data-testid="ben-totals-row">
                    <td className="px-4 py-3 text-xs uppercase tracking-wider text-slate-400">Totais</td>
                    <td className="px-4 py-3 font-mono text-slate-100" data-testid="ben-foot-fuel">{brl(totals.fuel)}</td>
                    <td className="px-4 py-3 font-mono text-slate-100" data-testid="ben-foot-meal">{brl(totals.meal)}</td>
                    <td className="px-4 py-3 font-mono text-slate-100" data-testid="ben-foot-commute">{brl(totals.commute)}</td>
                    <td className="px-4 py-3 font-mono text-slate-400" data-testid="ben-foot-km">{km(totals.km)}</td>
                    <td className="px-4 py-3 font-mono text-amber-300" data-testid="ben-foot-prod">{brl(totals.prod)}</td>
                    <td className="px-4 py-3 font-mono text-orange-300" data-testid="ben-foot-total">{brl(totals.fuel + totals.meal + totals.commute + totals.prod)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
            <Paginator pg={pg} testId="ben-pager" />
          </div>
        </>
      )}
      {detail && (() => {
        const b1 = computeAutoBonus(q1By[detail.id] || 0, minsFor(detail.id, mins), tiers, contractOf(detail.id));
        const b2 = computeAutoBonus(q2By[detail.id] || 0, minsFor(detail.id, mins), tiers, contractOf(detail.id));
        const monthLabel = data?.label || `${MONTHS[month - 1]}/${year}`;
        const quinz = (useQ1 && useQ2) ? "Mensal" : useQ1 ? "1ª quinzena" : "2ª quinzena";
        return (
          <BenefitDetailModal
            driver={detail}
            periodText={`${monthLabel} · ${quinz}`}
            start={start}
            end={end}
            prodQ1={b1.value}
            prodQ2={b2.value}
            levelQ1={b1.level}
            levelQ2={b2.level}
            useQ1={useQ1}
            useQ2={useQ2}
            onClose={() => setDetail(null)}
          />
        );
      })()}
    </div>
  );
}

const NUM_FIELDS_BENEFITS = [
  { k: "meal_voucher_value", label: "Vale alimentação (por dia)", prefix: "R$" },
  { k: "fuel_price", label: "Preço do combustível (litro)", prefix: "R$" },
  { k: "commute_ciclista", label: "Deslocamento — Ciclista", prefix: "R$" },
  { k: "commute_motociclista", label: "Deslocamento — Motoboy", prefix: "R$" },
  { k: "commute_administrativo", label: "Deslocamento — Administrativo", prefix: "R$" },
];
const NUM_FIELDS_LEVELS = [
  { k: "bronze_min", label: "Bronze (mín.)" },
  { k: "prata_min", label: "Prata (mín.)" },
  { k: "ouro_min", label: "Ouro (mín.)" },
  { k: "platina_min", label: "Platina (mín.)" },
  { k: "diamante_min", label: "Diamante (mín.)" },
  { k: "mestre_min", label: "Mestre (mín.)" },
];

function BaseCard({ base, onSaved }) {
  const [b, setB] = useState({ address: base.address ?? "", radius_m: base.radius_m ?? "", lat: base.lat ?? "", lng: base.lng ?? "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!String(b.address).trim()) { toast.error("Informe o endereço"); return; }
    setBusy(true);
    try {
      await api.put(`/admin/bases/${base.id}`, { address: b.address, radius_m: Math.max(1, parseInt(b.radius_m, 10) || 1), lat: Number(b.lat), lng: Number(b.lng) });
      toast.success(`${base.name} atualizada`);
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const field = (k, label, step) => (
    <div className="space-y-1.5">
      <Label className="text-slate-300">{label}</Label>
      <Input type="number" step={step} value={b[k]} onChange={(e) => setB({ ...b, [k]: e.target.value })} data-testid={`base-${base.id}-${k}`} className="border-slate-700 bg-slate-950 text-slate-100" />
    </div>
  );
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-4" data-testid={`base-card-${base.id}`}>
      <p className="mb-3 text-sm font-semibold text-orange-300">{base.name}</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1.5 sm:col-span-2 lg:col-span-4">
          <Label className="text-slate-300">Endereço</Label>
          <Input value={b.address} onChange={(e) => setB({ ...b, address: e.target.value })} data-testid={`base-${base.id}-address`} className="border-slate-700 bg-slate-950 text-slate-100" />
        </div>
        {field("radius_m", "Raio permitido (metros)", "1")}
        {field("lat", "Latitude", "any")}
        {field("lng", "Longitude", "any")}
        <div className="flex items-end">
          <Button onClick={save} disabled={busy} className="w-full bg-orange-500 text-white hover:bg-orange-600" data-testid={`base-${base.id}-save`}><Save className="h-4 w-4" /> Salvar base</Button>
        </div>
      </div>
    </div>
  );
}

function BasesSection() {
  const [bases, setBases] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => api.get("/admin/bases", { skipBaseId: true })
    .then((r) => { setBases(r.data?.bases || []); setErr(null); })
    .catch((e) => setErr(e));
  useEffect(() => { load(); }, []);
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid="bases-section">
      <h3 className="mb-4 text-base font-semibold text-slate-100">Bases da empresa (ponto/deslocamento)</h3>
      {err ? <ErrorState error={err} onRetry={load} /> : bases === null ? <Loading /> : (
        <div className="space-y-3">
          {bases.length === 0 && <Empty label="Nenhuma base cadastrada" />}
          {bases.map((b) => <BaseCard key={`${b.id}-${b.address}-${b.radius_m}-${b.lat}-${b.lng}`} base={b} onSaved={load} />)}
        </div>
      )}
    </div>
  );
}

function Rules() {
  const { data, loading, error, refetch } = useFetch("/config");
  const { selectedBaseId, bases } = useBase();
  const baseCfgs = useBaseConfigs();
  const hasOwn = !!(selectedBaseId && baseCfgs.data?.[selectedBaseId]);
  const baseName = (bases || []).find((b) => b.id === selectedBaseId)?.name;
  const { settings, save: savePanel } = usePayrollSettings();
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const freqs = useBenefitFrequencies();
  const freqScope = selectedBaseId || "global";
  const [freq, setFreq] = useState({});
  const tiersData = useFetch("/admin/bonus-tiers");
  const tiers = tiersData.data?.tiers || [];
  useEffect(() => { if (data) setF({ ...data, mestre_min: data.mestre_min ?? settings.mestre_min }); }, [data, settings.mestre_min]);
  useEffect(() => {
    const all = freqs.data || {};
    setFreq({ ...(all.global || {}), ...(all[freqScope] || {}) });
  }, [freqs.data, freqScope]);

  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  const setNum = (k, v) => setF((s) => ({ ...s, [k]: v === "" ? null : Number(v) }));

  const freqSelect = (k) => (
    <>
      <select value={freq[k] || "quinzena"} onChange={(e) => setFreq((s) => ({ ...s, [k]: e.target.value }))}
        data-testid={`cfg-freq-${k}`} className="h-9 w-full rounded-md border border-slate-700 bg-slate-950 px-2 text-sm text-slate-100">
        {FREQ_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
      <p className="text-[11px] text-slate-500" data-testid={`cfg-freq-hint-${k}`}>{FREQ_HINT[freq[k] || "quinzena"]}</p>
    </>
  );

  const numField = ({ k, label, prefix, withFreq }) => (
    <div key={k} className="space-y-1.5">
      <Label className="text-slate-300">{label}</Label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">{prefix}</span>}
        <Input type="number" step="0.01" value={f[k] ?? ""} onChange={(e) => setNum(k, e.target.value)}
          data-testid={`cfg-${k}`} className={`border-slate-700 bg-slate-950 text-slate-100 ${prefix ? "pl-9" : ""}`} />
      </div>
      {withFreq && freqSelect(k)}
    </div>
  );

  const prodField = (t) => {
    const k = prodKey(t.name);
    return (
      <div key={t.id} className="space-y-1.5" data-testid={`cfg-prod-${k}`}>
        <Label className="text-slate-300">Produtividade — {t.name}</Label>
        <div className="flex h-9 items-center rounded-md border border-slate-800 bg-slate-950/60 px-3 font-mono text-sm text-slate-200" data-testid={`cfg-prod-value-${k}`}>{brl(t.value)}</div>
        {freqSelect(k)}
      </div>
    );
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      [...NUM_FIELDS_BENEFITS, ...NUM_FIELDS_LEVELS].forEach(({ k }) => { body[k] = f[k]; });
      const freqBody = {};
      [...NUM_FIELDS_BENEFITS.map(({ k }) => k), ...tiers.map((t) => prodKey(t.name))].forEach((k) => { freqBody[k] = freq[k] || "quinzena"; });
      if (selectedBaseId) {
        await panelApi.put(`/panel/base-config/${selectedBaseId}`, body);
      } else {
        await api.put("/config", body);
        // O upstream descarta "mestre_min": persistimos no backend do painel.
        await savePanel({ mestre_min: f.mestre_min === "" || f.mestre_min == null ? null : Number(f.mestre_min) });
      }
      await panelApi.put(`/panel/benefit-frequency/${freqScope}`, freqBody);
      toast.success(selectedBaseId ? `Regras salvas para ${baseName || "a base"}` : "Regras globais atualizadas");
      await queryClient.invalidateQueries();
      refetch();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const resetBase = async () => {
    setBusy(true);
    try {
      await panelApi.delete(`/panel/base-config/${selectedBaseId}`);
      await panelApi.delete(`/panel/benefit-frequency/${selectedBaseId}`);
      toast.success("Base voltou a usar os valores globais");
      await queryClient.invalidateQueries();
      refetch();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className={`rounded-lg border px-4 py-2.5 text-sm ${selectedBaseId ? "border-sky-500/30 bg-sky-500/5 text-sky-200" : "border-slate-800 bg-slate-900 text-slate-400"}`} data-testid="cfg-scope">
        {selectedBaseId
          ? <>Editando <b>{baseName || "base selecionada"}</b> — {hasOwn ? "configuração própria desta base." : "ainda sem configuração própria: mostrando os valores globais (serão copiados ao salvar)."}
              {hasOwn && <button onClick={resetBase} disabled={busy} className="ml-2 text-sky-300 underline hover:text-sky-100" data-testid="cfg-reset-base">Usar valores globais</button>}</>
          : <>Editando os <b>valores globais</b> (usados pelas bases sem configuração própria e pelo app mobile). Selecione uma base no topo para configurar só ela.</>}
      </div>
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
        <h3 className="mb-4 text-base font-semibold text-slate-100">Valores de benefícios</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {NUM_FIELDS_BENEFITS.map((x) => numField({ ...x, withFreq: true }))}
        </div>
        <h4 className="mb-3 mt-6 flex items-center gap-2 text-sm font-semibold text-slate-200"><Trophy className="h-4 w-4 text-amber-400" /> Benefícios de produtividade</h4>
        {tiersData.loading ? <Loading /> : tiers.length === 0 ? <Empty label="Nenhuma categoria de desempenho cadastrada" /> : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="cfg-prod-section">
            {tiers.map(prodField)}
          </div>
        )}
        <p className="mt-3 text-xs text-slate-500">O valor de cada categoria é editado em Folha → “Categorias de desempenho”. Aqui você define só a frequência de aplicação.</p>
      </div>
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
        <h3 className="mb-4 text-base font-semibold text-slate-100">Níveis por entregas (quinzena)</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {NUM_FIELDS_LEVELS.map(numField)}
        </div>
        <p className="mt-3 text-xs text-slate-500">Mínimo de entregas na quinzena para cada nível. O valor do bônus de cada nível é definido em Folha → “Categorias de desempenho”. <span className="text-amber-300/80">“Mestre (mín.)” é salvo apenas neste painel</span> (o sistema atual não armazena este campo).</p>
      </div>
      <Button onClick={save} disabled={busy} className="bg-orange-500 text-white hover:bg-orange-600" data-testid="cfg-save"><Save className="h-4 w-4" /> Salvar regras</Button>
      <BasesSection />
    </div>
  );
}

export default function Beneficios() {
  const { user } = useAuth();
  const showConfig = can(user, "beneficios_config");
  return (
    <div data-testid="beneficios">
      <Tabs defaultValue="relatorio">
        <TabsList className="bg-slate-900">
          <TabsTrigger value="relatorio" data-testid="ben-tab-relatorio"><Gift className="mr-1.5 h-4 w-4" /> Relatório</TabsTrigger>
          {showConfig && (
            <TabsTrigger value="regras" data-testid="ben-tab-regras"><SlidersHorizontal className="mr-1.5 h-4 w-4" /> Regras & Configurações</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="relatorio" className="mt-4"><Report /></TabsContent>
        {showConfig && <TabsContent value="regras" className="mt-4"><Rules /></TabsContent>}
      </Tabs>
    </div>
  );
}
